// Copy is carried over from the current craft-tower.com site (lib/landing-page-content.ts,
// Navbar, ContactModal, page metadata). New strings exist only for UI the old site did not have.

export const en = {
  meta: {
    title: 'CRAFT TOWER DOO | Software Development & IT Services in Montenegro',
    description:
      'CRAFT TOWER DOO is a software and IT company in Montenegro. We build websites, apps, automation, custom business systems, and secure infrastructure for individuals and companies.',
  },
  nav: {
    services: 'Services',
    work: 'Work',
    process: 'Process',
    about: 'About us',
    contact: 'Contact',
    menu: 'Menu',
    close: 'Close',
    skip: 'Skip to content',
  },
  ui: {
    language: 'Language',
    theme: 'Switch light / dark theme',
    cyber: 'Cyber mode',
    cyberHint: 'GO CYBER,\nTRY ME!',
    cyberBoot: '> JACKING IN… GRID ONLINE_',
    localTime: 'Bar',
    backToTop: 'Back to top',
    loading: 'Assembling',
  },
  hero: {
    badge: 'Software studio in Bar, Montenegro',
    title: 'Reliable software for',
    titleAccent: 'real work',
    description:
      'In CRAFT TOWER we design and build websites, business tools, automations, and technical setups for people and growing companies in Montenegro and abroad.',
    ctaPrimary: 'Start Your Project',
    ctaSecondary: 'Selected work',
    slogan: 'Crafting code. Building the future.',
  },
  proofs: [
    { label: 'Founded', value: '13.08.2021' },
    { label: 'Base', value: 'Bar, Montenegro' },
    { label: 'Languages', value: 'English, Montenegrin, Russian' },
    { label: 'Focus', value: 'Custom software and IT support' },
  ],
  marquee: ['Websites', 'Booking systems', 'Client portals', 'Admin dashboards', 'AI workflows', 'Automation', 'Networks', 'IT support'],
  services: {
    label: 'Services',
    title: 'Websites, internal tools, automation, and IT support.',
    description:
      "Whether it's a fast company website, a booking system, a client portal, an admin dashboard, or a smarter internal workflow, CRAFT TOWER builds practical systems for everyday use.",
    individualsButton: 'For Individuals',
    businessButton: 'For Small Businesses',
    focus: 'In focus',
    individuals: [
      {
        eyebrow: 'Personal projects',
        title: 'Websites, dashboards, and private tools',
        summary: 'For freelancers, creators, families, and niche communities that need something more useful than a generic template.',
        bullets: ['Personal websites and portfolios', 'Private trackers, dashboards, and portals', 'Forms, maps, bots, and API integrations'],
        focus: ['Modern portfolio websites', 'Personalized custom dashboards', 'RPG or board games trackers and helpers'],
      },
      {
        eyebrow: 'Practical support',
        title: 'IT setup and ongoing digital help',
        summary: 'For people who want a reliable setup, safer devices, and less friction in daily digital work.',
        bullets: ['Device and software setup', 'Backups, passwords, and home network hygiene', 'Hands-on support for AI and automation tools'],
        focus: ['Only what you really need for work', 'Safety is top priority', 'Speed up and automate your daily routine'],
      },
    ],
    business: [
      {
        eyebrow: 'Customer-facing systems',
        title: 'Web presence, booking, and lead capture',
        summary: 'For small businesses that need a stronger first impression and smoother customer flows.',
        bullets: ['Company websites and landing pages', 'Booking, inquiry, and quote flows', 'SEO-ready structure and analytics setup'],
        focus: ['Modern design with SEO best practices', 'Effective and useful dashboards', 'Analyze data and improve performance'],
      },
      {
        eyebrow: 'Internal operations',
        title: 'Business tools, automations, and infrastructure',
        summary: 'For teams that need lightweight systems to reduce manual work and keep operations organized.',
        bullets: ['Custom admin panels and internal tools', 'AI-assisted workflows and automations', 'Network, device, and cloud support'],
        focus: ['Clean customized interfaces', 'Most effective automated workflows', 'Reliable technical support'],
      },
    ],
  },
  work: {
    label: 'Work',
    title: 'Selected work',
    description:
      'Screens, promo videos, and the stories behind the products we build. Here is some of our recent work.',
    projects: 'Projects',
    loadMore: 'Load more',
    soon: 'Soon: more coming',
    highlights: 'What we built',
    viewCase: 'View case',
    status: { concept: 'Concept', 'in-progress': 'In progress', live: 'Live' },
    links: { live: 'Live site', case: 'Case study', repo: 'Source code', store: 'App store' },
    promo: 'Watch promo',
    outcome: 'Outcome',
    scope: 'Scope',
    stack: 'Stack',
    client: 'Client',
    year: 'Year',
    category: 'Category',
    screens: 'Screens',
    screensSoon: 'Screenshots and a promo video for this project will be added soon.',
    prevScreen: 'Previous screenshot',
    nextScreen: 'Next screenshot',
    prev: 'Previous project',
    next: 'Next project',
    close: 'Close',
  },
  process: {
    label: 'Process',
    title: 'How projects move forward',
    readout: 'Build progress',
    phases: ['Discover', 'Design', 'Build', 'Test', 'Launch'],
    steps: [
      'Discovery call and scope outline',
      'Draft UI direction and architecture plan',
      'Core engineering and milestone builds',
      'Quality assurance, performance, and security testing',
      'Production launch and ongoing practical support',
    ],
  },
  about: {
    label: 'About us',
    title: 'Our vision',
    p1: "Technology shouldn't feel like navigating a maze in the dark. At CRAFT TOWER DOO, we believe IT and programming are about building reliable tools that make your life and business in Montenegro run smoother.",
    p2: "Whether you need a custom web app from scratch, AI in your daily workflow, or a secure lightning-fast network, we strip away the corporate jargon. We deliver clean code, rock-solid support, and solutions that just work. Let's build something practical together.",
    sign: 'Andrey D',
    role: 'Founder',
    checklistTitle: 'How the work is meant to feel',
    checklist: [
      'Clear scope before coding starts',
      'Draft-first collaboration instead of guesswork',
      'Direct communication with the person building the solution',
    ],
    company: 'Company',
    founded: 'Founded',
    address: 'Address',
    addressValue: 'Šušanj b.b., 85000 Bar, Montenegro',
  },
  contact: {
    label: 'Contact',
    titleLead: 'Build something reliable, practical, and',
    titleAccent: 'fast.',
    description:
      'CRAFT TOWER DOO is based in Bar, Montenegro and focused on software engineering and IT support that create measurable value for people and companies.',
    cta: 'Discuss Your Project',
    email: 'Email',
    phone: 'Phone / Viber / WhatsApp',
    web: 'Web',
    address: 'Address',
    form: {
      title: 'Send a',
      highlight: 'Message',
      subtitle: "Have a project in mind? Let's talk about it.",
      name: 'Full Name',
      email: 'Email Address',
      message: 'Message',
      namePlaceholder: 'John Doe',
      emailPlaceholder: 'john@example.com',
      messagePlaceholder: 'Tell me about your project...',
      send: 'Send Message',
      sending: 'Sending…',
      success: 'Message sent successfully! I will get back to you soon.',
      error: 'Something went wrong. Please try again.',
      invalid: 'Please fill in all fields with a valid email.',
    },
  },
  agent: {
    home: 'Hi! Need a website, a tool or an automation?',
    services: "Pick who it's for — I'll show what fits.",
    work: 'Our first real case, MonteGigs, is up front. More are on the way!',
    process: "Floor by floor — that's how we build.",
    about: 'Clear scope, honest drafts, direct contact.',
    contact: "Tell us about your idea — we'll get back to you.",
    sendMessage: 'Send a Message',
    openChat: 'Live Chat',
    onlineStatus: 'Online',
    badgeText: 'CT-AGENT',
  },
  cookie: {
    badge: 'Privacy & Security',
    title: 'We value your security & privacy',
    description:
      'We use cookies and local storage for security, theme & language preferences, and live support chat sessions. No cross-site ads or third-party trackers.',
    agentNote: 'Encrypted & secure: your preferences are stored locally on your device.',
    acceptAll: 'Accept All',
    essentialOnly: 'Essential Only',
    managePreferences: 'Cookie Details & Privacy',
    modalTitle: 'Privacy & Cookie Settings',
    modalSubtitle: 'Full transparency on how CRAFT TOWER DOO handles local storage and cookies.',
    strictlyNecessary: 'Strictly Necessary & Security',
    strictlyNecessaryDesc: 'Required for core site operation, language routing, CSRF protection, and theme storage. Cannot be disabled.',
    liveChat: 'Live Support Chat',
    liveChatDesc: 'Anonymous session token so you can speak directly with our team via Telegram.',
    analytics: 'Performance & Telemetry',
    analyticsDesc: 'Anonymous speed metrics to keep load times lightning-fast in Montenegro and globally.',
    alwaysActive: 'Always active',
    dataController: 'Data Controller: CRAFT TOWER DOO (Šušanj b.b., 85000 Bar, Montenegro) · info@craft-tower.com',
    savePreferences: 'Save Preferences',
    close: 'Close',
    footerLink: 'Privacy & Cookies',
  },
  chat: {
    title: 'Craft Tower Live Chat',
    subtitle: 'Ask us anything in real-time',
    online: 'Online',
    welcomeMsg: 'Hello! How can we help build your vision today?',
    namePromptTitle: 'Welcome to Live Chat',
    namePromptSubtitle: 'Please enter your name to start chatting:',
    namePlaceholder: 'Enter your name...',
    nameSubmit: 'Start Chat',
    chattingAs: 'Chatting as',
    edit: 'Edit',
    placeholder: 'Type your message...',
    send: 'Send',
    sending: 'Sending…',
    adminTitle: 'Craft Tower Team',
    aiAssistantTitle: 'CT-Agent AI (Gemma 4)',
    you: 'You',
    modeAi: 'AI Assistant',
    modeHuman: 'Live Team',
    modeHumanNotice: 'You are in Live Team mode. Leave your message and our team will reply directly via Telegram during business hours (10:00–16:00)!',
    roundBadge: 'Round',
    limitNotice: 'Daily consultation limit reached (10/10 rounds). Our team has received your transcript on Telegram and will follow up during business hours (10:00–16:00).',
    sessionTerminatedNotice: 'Session ended due to out-of-scope inquiries. Transcripts forwarded to info@craft-tower.com.',
    unreadMsg: 'New message from Craft Tower Team',
    offlineNotice: 'Live chat is currently unavailable. You can also send a message via the form or email info@craft-tower.com.',
    notificationsTooltip: 'Enable desktop notifications for operator replies',
    notificationsActive: 'Notifications active',
    soundMute: 'Mute chat sounds',
    soundUnmute: 'Unmute chat sounds',
    export: 'Export Transcript',
    downloadMd: 'Download Markdown (.md)',
    downloadPdf: 'Print / Save as PDF',
    emailSummary: 'Email Intake Summary',
    emailPromptTitle: 'Receive Consultation Summary',
    emailPromptSubtitle: 'Enter your email to receive this consultation transcript and inquiry intake summary:',
    emailPlaceholder: 'your@email.com',
    emailSubmit: 'Send Intake Summary',
    emailSentSuccess: 'Intake summary and transcript sent to your email!',
    systemNotice: 'System Notice',
    verificationNotice: 'Security verification could not be completed. If you are using an ad-blocker or privacy extension, please allow challenges.cloudflare.com or try again.',
  },
  footer: {
    made: 'Designed & engineered in Bar, Montenegro.',
    rights: 'All rights reserved.',
    oldSite: 'Old site',
  },
  // Alternative copy shown while Cyber mode is active (from the current site's cyberOverrides).
  cyber: {
    hero: {
      title: 'The digital frontier is',
      titleAccent: 'already here',
      description:
        "Welcome to the edge. We aren't just writing code—we're architecting high-performance digital systems for the modern era.",
      slogan: 'Clean code. Artificial intelligence. Welcome to the grid.',
    },
    about: {
      p1: "Technology shouldn't hold you back. Whether you're automating the grind with AI, deploying a hardened server network, or spinning up a seamless web interface, we strip away the corporate static.",
      p2: "We deliver clean code, secure systems, and high-voltage solutions that put you ahead of the curve. Let's jack in and build something extraordinary.",
    },
  },
}
